import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Anchor, Send, Check, Users, BookOpenCheck, Sparkles } from "lucide-react";
import Navigation from "../components/Navigation";
import SpeakerButton from "../components/SpeakerButton";
import LegacyPromotion from "../components/LegacyPromotion";
import {
  fetchMentors, fetchMentorMessages, postMentorMessage, markMessageRead, fetchCadet, fetchCurriculum
} from "../lib/api";

export const Mentors = () => {
  const [mentors, setMentors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [cadet, setCadet] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [body, setBody] = useState("");
  const [moduleTag, setModuleTag] = useState("");
  const [sending, setSending] = useState(false);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [postAsPrivateer, setPostAsPrivateer] = useState(false);

  const refresh = async () => {
    setMentors(await fetchMentors());
    setMessages(await fetchMentorMessages());
  };

  useEffect(() => {
    refresh();
    fetchCadet().then(setCadet);
    fetchCurriculum().then(d => setModules(d.modules || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!selectedMentor || !body.trim()) return;
    setSending(true);
    try {
      await postMentorMessage({
        mentor_id: selectedMentor.id,
        body: body.trim(),
        module_id: moduleTag || null,
        sender: postAsPrivateer ? "mentor" : "cadet",
      });
      setBody("");
      setModuleTag("");
      await refresh();
      // Poll for AI reply (up to ~12s) when cadet posted to a non-legacy mentor
      if (!postAsPrivateer && !selectedMentor.is_legacy) {
        setAwaitingReply(true);
        const before = (await fetchMentorMessages()).length;
        for (let i = 0; i < 6; i++) {
          await new Promise(r => setTimeout(r, 2000));
          const latest = await fetchMentorMessages();
          if (latest.length > before) { setMessages(latest); break; }
        }
        setAwaitingReply(false);
      }
    } finally { setSending(false); }
  };

  const myPrivateer = mentors.find(m => m.is_legacy && m.legacy_cadet_id === cadet?.id);
  const canPostAsPrivateer = !!myPrivateer && selectedMentor?.id === myPrivateer.id;

  const markRead = async (id) => {
    await markMessageRead(id);
    setMessages(messages.map(m => m.id === id ? { ...m, read: true } : m));
  };

  return (
    <div className="min-h-screen eigoo-stage-bg">
      <Navigation cadet={cadet} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-yellow-300/30 bg-yellow-300/5 text-yellow-300 font-display font-bold tracking-[0.3em] text-[10px] md:text-xs mb-4">
            <Anchor size={14} /> MENTOR-CADET BRIDGE
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black text-4xl md:text-6xl leading-tight">
              The <span className="text-yellow-300">Privateers</span>
            </h1>
            <SpeakerButton text="This is the Mentor-Cadet Bridge. Privateers are your guides. Pick one and send a signal to ask for help or share your wins." testid="speaker-mentors-intro" size="md" />
          </div>
          <p className="text-white/70 mt-2 text-base md:text-lg max-w-2xl">
            Privateers are elite mentors in the Sovereign Fleet. Pick one, ask a question, or post a win. They will signal back — in character — within seconds.
          </p>
        </motion.section>

        {/* Legacy Cohort promotion */}
        <section className="mb-8" data-testid="legacy-section">
          <LegacyPromotion
            cadet={cadet}
            mentors={mentors}
            onPromoted={() => { refresh(); }}
          />
        </section>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Mentor roster */}
          <div className="lg:col-span-1">
            <div className="eigoo-glass rounded-3xl p-5 md:p-6" data-testid="mentor-roster">
              <div className="flex items-center gap-2 mb-4">
                <Users size={18} className="text-yellow-300" />
                <div className="font-display font-bold text-xl">Privateer Roster</div>
              </div>
              <ul className="space-y-3">
                {mentors.map((m) => {
                  const active = selectedMentor?.id === m.id;
                  return (
                    <li key={m.id}>
                      <button
                        onClick={() => setSelectedMentor(m)}
                        className={`w-full text-left flex items-center gap-3 p-3 rounded-2xl border transition-all ${active ? "bg-yellow-300/10 border-yellow-300/50 eigoo-glow-gold" : "bg-black/30 border-white/10 hover:border-yellow-300/30"}`}
                        data-testid={`mentor-${m.id}`}
                      >
                        <div className={`h-12 w-12 rounded-xl flex items-center justify-center font-display font-black text-xl ${active ? "bg-gradient-to-br from-yellow-300 to-yellow-600 text-black" : "bg-white/5 text-yellow-300 border border-yellow-300/20"}`}>
                          {m.avatar_emoji || m.name?.[0] || "P"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold text-base truncate flex items-center gap-1.5">
                            {m.name}
                            {m.is_legacy && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-yellow-300 text-black font-display font-black">Legacy</span>}
                          </div>
                          <div className="text-xs text-yellow-300/80 truncate">{m.title}</div>
                        </div>
                        <SpeakerButton text={`${m.name}. ${m.title}. ${m.bio}`} testid={`speaker-mentor-${m.id}`} size="sm" voice={m.voice || "fable"} />
                      </button>
                      {active && (
                        <div className="mt-2 px-3 text-sm text-white/70 italic leading-relaxed">
                          "{m.bio}"
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Bridge / messages */}
          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={send} className="eigoo-glass rounded-3xl p-5 md:p-6" data-testid="bridge-form">
              <div className="flex items-center gap-2 mb-3">
                <Send size={18} className="text-yellow-300" />
                <div className="font-display font-bold text-xl">Send a Signal</div>
                <SpeakerButton text="Pick a Privateer from the roster. Type your question or win. Tap send to signal them." testid="speaker-send-signal" size="sm" />
              </div>
              <div className="text-sm text-white/60 mb-3">
                {selectedMentor
                  ? <>Signaling <span className="text-yellow-200 font-bold">{selectedMentor.name}</span>{!postAsPrivateer && !selectedMentor.is_legacy && <span className="text-yellow-300/70 ml-2">· AI reply expected within 10 seconds</span>}</>
                  : "Pick a Privateer from the roster to begin."}
              </div>
              {canPostAsPrivateer && (
                <label className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-yellow-300/5 border border-yellow-300/30 cursor-pointer" data-testid="post-as-privateer-toggle">
                  <input
                    type="checkbox"
                    checked={postAsPrivateer}
                    onChange={(e) => setPostAsPrivateer(e.target.checked)}
                    className="accent-yellow-300"
                  />
                  <span className="text-sm">Post as <span className="text-yellow-200 font-bold">{myPrivateer?.name}</span> (Legacy mode — for journaling and mentor practice)</span>
                </label>
              )}
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Ask a question, share a win, or request guidance..."
                rows={4}
                className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 focus:border-yellow-300/60 outline-none resize-none text-base"
                data-testid="signal-body"
              />
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <select
                  value={moduleTag}
                  onChange={(e) => setModuleTag(e.target.value)}
                  data-testid="signal-module-select"
                  className="px-3 py-2 rounded-xl bg-black/40 border border-yellow-300/20 outline-none text-sm"
                >
                  <option value="">Tag a module (optional)</option>
                  {modules.map((m) => <option key={m.id} value={m.id}>Module {m.numeral} — {m.title}</option>)}
                </select>
                <button
                  type="submit"
                  disabled={!selectedMentor || !body.trim() || sending}
                  className="btn-eigoo btn-gold ml-auto"
                  data-testid="submit-signal"
                >
                  <Send size={18} /> {sending ? "Sending..." : "Send Signal"}
                </button>
              </div>
              {awaitingReply && (
                <div className="mt-3 flex items-center gap-2 text-yellow-300 text-sm" data-testid="awaiting-reply">
                  <Sparkles size={14} className="animate-pulse" />
                  <span>{selectedMentor?.name} is composing a signal...</span>
                  <span className="soundwave"><span/><span/><span/><span/><span/></span>
                </div>
              )}
            </form>

            <div className="eigoo-glass rounded-3xl p-5 md:p-6" data-testid="message-feed">
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <BookOpenCheck size={18} className="text-yellow-300" />
                  <div className="font-display font-bold text-xl">Mentor Signal Feed</div>
                  <SpeakerButton text={`You have ${messages.length} signals in your feed.`} testid="speaker-feed" size="sm" />
                </div>
                <div className="text-xs uppercase tracking-widest text-white/50">{messages.filter(m => !m.read).length} unread</div>
              </div>
              {messages.length === 0 ? (
                <div className="text-center py-8 text-white/50">
                  No signals yet. Send one to a Privateer above to start the exchange.
                </div>
              ) : (
                <ul className="space-y-3">
                  <AnimatePresence>
                    {messages.map(msg => {
                      const mod = modules.find(m => m.id === msg.module_id);
                      const fromCadet = msg.sender === "cadet";
                      const containerCls = fromCadet
                        ? "ml-auto max-w-[85%] bg-red-500/10 border-red-500/30"
                        : msg.read
                          ? "max-w-[85%] bg-black/20 border-white/5"
                          : "max-w-[85%] bg-yellow-300/5 border-yellow-300/30 eigoo-glow-gold";
                      return (
                        <motion.li
                          key={msg.id}
                          layout
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`rounded-2xl border p-4 ${containerCls}`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-display font-black shrink-0 ${
                              fromCadet
                                ? "bg-gradient-to-br from-red-500 to-red-700 text-white"
                                : "bg-gradient-to-br from-yellow-300 to-yellow-600 text-black"
                            }`}>
                              {fromCadet ? (cadet?.callsign?.[0] || "C") : (msg.mentor_avatar || msg.mentor_name?.[0] || "P")}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-display font-bold">
                                  {fromCadet ? `${cadet?.callsign || "Cadet"} → ${msg.mentor_name}` : msg.mentor_name}
                                </div>
                                <div className={`text-[10px] uppercase tracking-widest ${fromCadet ? "text-red-300" : "text-yellow-300/70"}`}>
                                  {fromCadet ? "Cadet Signal" : msg.mentor_title}
                                </div>
                                {msg.ai_generated && (
                                  <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-yellow-300/15 border border-yellow-300/40 text-yellow-200 font-display font-bold flex items-center gap-1">
                                    <Sparkles size={9} /> AI · in-character
                                  </span>
                                )}
                                {mod && (
                                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-yellow-300/10 border border-yellow-300/30 text-yellow-200">Module {mod.numeral}</span>
                                )}
                                {!msg.read && !fromCadet && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-300">New</span>}
                              </div>
                              <div className="text-white/90 mt-1.5 leading-relaxed">{msg.body}</div>
                              <div className="flex items-center gap-2 mt-2">
                                <SpeakerButton text={`${msg.mentor_name} says: ${msg.body}`} testid={`speaker-msg-${msg.id}`} size="sm" voice={msg.mentor_voice || "fable"} />
                                {!msg.read && (
                                  <button onClick={() => markRead(msg.id)} className="text-xs text-yellow-300 hover:underline flex items-center gap-1" data-testid={`mark-read-${msg.id}`}>
                                    <Check size={12} /> Mark read
                                  </button>
                                )}
                                <span className="ml-auto text-[11px] text-white/40">{new Date(msg.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
                              </div>
                            </div>
                          </div>
                        </motion.li>
                      );
                    })}
                  </AnimatePresence>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mentors;
